import sizeComparison from "assets/icons/psp/illustration-multiple-sites.svg";
import KayakImage from "assets/images/psp/richconentImages/Store-kayaks-in-storage-unit-best-practices.webp";
import ctaMan from "assets/images/psp/inlineCTA.jpg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import KayakStorage from "assets/images/psp/relatedContentImages/kayak-storage.webp";
import DriveUpStorage from "assets/images/psp/relatedContentImages/driveupstorage-jpg.webp";
import GarageStorage from "assets/images/psp/relatedContentImages/garage-vs-storage.webp";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import HeaderBackgroundDesktop from "assets/images/psp/hero/kayak-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/kayak-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/kayak-header-tablet.webp";
import { storageCategories } from "../utils/getPSPLinks";

const kayakRichContent = `
Proper storage preparation keeps kayaks in pristine condition:

1. **Before Storage**
    - Clean kayaks thoroughly to remove sand, salt, and organic material
    - Inspect for damage and repair small cracks before storage
    - Remove electronics, batteries, and perishable items
    - Loosen or remove bungee cords to prevent permanent stretching
    - Store paddles separately to avoid warping

1. **Storage Positioning**
    - Suspend kayaks or use padded cradles to prevent hull deformation
    - Avoid storing kayaks flat on floors for extended periods
    - Keep canoes upside-down or on their sides to prevent water accumulation
    - Position boats away from direct sunlight if storing near unit doors

1. **Climate Considerations**
    - Standard units work well for plastic kayaks in most climates
    - Consider climate control ($35-$60/month) for composite, fiberglass, or wooden canoes
    - Temperature control prevents warping, cracking, and material degradation in extreme climates
`;

const contentSectionTwo = `
- **Apartment & Condo Dwellers**

  No garage or outdoor space available, building restrictions on balcony storage, and a secure solution for expensive equipment.
    
- **Multiple Boat Owners**

  Storage for fishing, touring, and whitewater kayaks, family collections with kids' boats, gear, and paddle organization.

- **Seasonal Paddlers**

  Winter storage protects boats from weather, off-season space reclamation at home, and the prevention of UV damage and warping.

- **Touring & Expedition Paddlers**

  Long-term storage for specialized touring canoes, protection for expensive composite and carbon fiber boats, and gear consolidation.

- **Rental & Tour Operators**

  Fleet storage for rental businesses, organized inventory management, and secure parking near launch sites.
`;

const contentSectionThree = `
Unlike home storage solutions, professional facilities offer specialized advantages:

- **Vertical Clearance**

  Standard 8–10 foot ceilings accommodate kayaks stored vertically, maximizing space efficiency in 5x5 units at just $44.81/month average

- **Ground-Level Access**

  Drive-up units let you load/unload boats easily without stairs or elevators

- **Security Features**

  Gated access and surveillance protect expensive kayaks from theft—critical for $1,000+ touring or fishing kayaks

- **Flexible Sizing**

  Start small with a 5x5 unit ($44.81/month) and upgrade to 10x10 ($97.40/month) as your collection grows—Storage.com's transparent pricing makes planning easy

- **Location Convenience**

  Many facilities are located near lakes, rivers, and popular launch sites, potentially saving gas money compared to storing boats at home

- **Climate Options**

  Choose standard units for recreational kayaks or add climate control ($35–$60/month) for high-end composite boats

- **Extended Access**

  Some facilities offer extended or [24/7 access](https://www.storage.com/blog/benefits-of-24-hour-storage/), so you can grab your PWC whenever the paddling mood strikes.

- **Cost Comparison**

  At $44.81–$63.05 monthly, kayak storage rental costs less than many monthly subscriptions while protecting investments worth hundreds or thousands of dollars
`;

const KayakFAQs = [
	{
		question: "What size storage unit do I need for kayaks?",
		answer: `According to Storage.com's national averages, a 5x5 unit ($44.81/month) accommodates 1-2 kayaks stored vertically with paddles and gear. For 2-3 boats or one canoe plus equipment, a 5x10 unit ($63.05/month) works well. Serious paddlers with 4+ kayaks should consider 10x10 units at $97.40/month.`
	},
	{
		question: "How much does kayak storage rental cost?",
		answer: `Storage.com reports that small units (5x5) average $44.81 per month and medium units (5x10) average $63.05 per month nationwide. Larger 10x10 units for serious paddlers average $97.40 per month. Annual costs range from $537.72 to $1,168.80, depending on size.`
	},
	{
		question: "Can I store kayaks vertically in a storage unit?",
		answer: `Yes. Vertical storage is one of the best kayak storage ideas for maximizing space. Storage.com's 5x5 units ($44.81/month) provide 8–10 foot ceilings suitable for storing a kayak vertically with proper support at hull contact points.`
	},
	{
		question: "Do I need climate control for kayak storage?",
		answer: `Plastic recreational kayaks usually don't require climate control in most climates. Climate-controlled units (roughly $35–$60/month) are recommended for composite, fiberglass, or wooden canoes in extreme climates to prevent warping and material degradation.`
	},
	{
		question: "How should I prepare my kayak for storage?",
		answer: `Clean thoroughly, repair any damage, remove batteries and electronics, loosen bungee cords, and ensure kayaks are dry before storing. Support boats properly to prevent hull deformation during long-term storage—improper storage can cause permanent warping requiring costly repairs.`
	}
];

const kayakHerodescription = `
Your kayaking adventures shouldn't end with a storage struggle. Kayaks hanging from garage rafters create hazards, outdoor storage racks fade hulls, and apartment dwellers have nowhere to store 12-foot boats between river trips. Quality kayaks and canoes deserve better than weathering in backyards or cluttering living spaces. Canoe and kayak storage facilities provide secure, accessible solutions without sacrificing your home space. 
Storage.com connects paddlers with verified kayak storage rental facilities nationwide, offering flexible options from [small lockers](https://storage.com/blog/smallest-storage-unit-mini-storage-guide/) to [large units accommodating multiple boats.](https://www.storage.com/blog/what-is-maxi-storage/)
`;

export const PSPKayakContent = {
	type: "Kayak",
	sections: [
		{
			title: "Kayak/Canoe hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Kayak & Canoe Storage Units",
						description: kayakHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Kayak Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Kayak Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Kayak and Canoe Storage content module",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Kayak Storage Rental Costs",
						description:
							"Kayak storage rental through storage facilities is surprisingly affordable.",
						background: "white",
						content: [
							{
								id: "1",
								value: "According to Storage.com's 2025 national averages, small units perfect for kayak storage ideas cost $44.81 monthly for [5x5](https://www.storage.com/blog/5x5-storage-unit-size-guide/) units and $63.05 monthly for [5x10](https://www.storage.com/blog/5x10-storage-unit-size-guide/) units—far more practical than building dedicated storage structures or letting boats deteriorate outdoors."
							},
							{
								id: "2",
								value: "For paddlers with temperature-sensitive composite or wooden boats, climate-controlled small units cost $35-$60 monthly."
							},
							{
								id: "3",
								value: "Storage facilities solve these problems with vertical storage possibilities, climate options, and secure environments. Instead of garage gymnastics or weather-damaged boats, you get professional storage that keeps kayaks ready for your next paddling adventure."
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Storage Size Comparison",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						title: "Kayak and Canoe Storage Solutions by Collection Size",
						description:
							"Here are some of the most common kayak and canoe storage size options available. For [racing-quality or higher-end personal watercrafts](https://www.storage.com/blog/boat-storage-costs-complete-guide/), you may also want to consider climate controlled options.",
						image: sizeComparison,
						sizes: [
							{
								id: "5x5",
								label: "5x5",
								title: "",
								description:
									" - Small unit\n" +
									" - **Fits:** 1-2 kayaks stored vertically or one canoe\n" +
									" - **Best For:** Single paddler, recreational kayaks\n"
							},
							{
								id: "5x10",
								label: "5x10",
								title: "",
								description:
									" - Medium unit\n" +
									" - **Fits:** 2-3 kayaks plus paddles and gear\n" +
									" - **Best For:** Enthusiasts, small families\n"
							},
							{
								id: "10x10",
								label: "10x10",
								title: "",
								description:
									" - Large unit\n" +
									" - **Fits:** 4+ kayaks, multiple canoes, full gear collection\n" +
									" - **Best For:** Serious paddlers, rental businesses\n"
							},
							{
								id: "10x15",
								label: "10x15",
								title: "",
								description:
									" - Extra-large unit\n" +
									" - **Fits:** Fleet storage, tour operators, and extensive collections\n" +
									" - **Best For:** Commercial use, large families\n"
							}
						]
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Storage Content Section",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Who Needs Kayak and Canoe Storage?",
						description:
							"From weekend warriors to avid PWC collectors, storing your kayak in a storage unit is a safe, affordable alternative to building storage sheds or stuffing your paddlers in an already crowded garage. Here’s a quick list of who can benefit from storing kayaks and canoes in a storage unit:",
						content: [
							{
								id: "1",
								value: contentSectionTwo
							}
						],
						background: "blue",
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Storage Size Chart",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "Kayak Storage Ideas: How to Make the Most of Your Unit",
						description:
							"[Professional storage units offer flexibility and organizational opportunities you can’t find in a small apartment or house](https://www.storage.com/blog/how-to-organize-your-home-or-apartment-in-2020/). Here are smart kayak storage ideas using verified pricing data:",
						storages: [
							{
								title: "5x5",
								bestFor: [
									"Vertical storage",
									"1 kayak/canoe",
									"Paddles"
								]
							},
							{
								title: "5x10",
								bestFor: [
									"Vertical and horizontal storage",
									"Full gear storage"
								]
							},
							{
								title: "10x10",
								bestFor: [
									"Multiple kayaks/canoes",
									"Full kit storage"
								]
							},
							{
								title: "10x15",
								bestFor: [
									"Outdoor business storage",
									"Fleet storage"
								]
							},
							{
								title: "Climate Controlled",
								bestFor: [
									"Composite or fiberglass kayaks",
									"Wooden canoes"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Content Section",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Key Kayak Storage Strategy Takeaways",
						description: "",
						background: "blue",
						content: [
							{
								id: "1",
								value: `- **Single Paddlers**: 5x5 units at $44.81/month provide affordable storage for storing a kayak in a storage unit with essential gear

- **Small Families**: 5x10 units at $63.05/month are the most popular size for canoe and kayak storage with 2-3 boats

- **Serious Enthusiasts**: 10x10 units at $97.40/month handle multiple boat types (fishing, touring, whitewater) economically

- **Tour Operators**: 10x15 units at $124.07/month offer the best per-boat value for fleet storage

- **Premium Boats**: Climate control ($35-$60/month) protects expensive composite and wooden canoes from temperature extremes`
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Rich Content Section",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Storing Kayak in Storage Unit: Best Practices",
						description: "",
						mediaUrl: KayakImage,
						mediaType: "image",
						additionalContent: true,
						markDown: kayakRichContent,
						textAlign: "left"
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Content Section",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "What Makes Canoe and Kayak Storage Facilities Different",
						description: "",
						content: [
							{
								id: "1",
								value: contentSectionThree
							}
						],
						background: "light-blue",
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Inline CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Kayak / Canoe",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						background: "white",
						imageAltText: "inlineCTA image"
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: {
						title: "Storage.com has 4.4 out of 5 star rating on",
						testimonials: [
							{
								body: "They were great. Found multiple units for me and helped me find the one I needed.",
								author: "Michael Quintana",
								title: "Finding units on the fly",
								imageSrc: GeorgeRansom
							},
							{
								body: "I had never used a storage unit before and had many questions regarding the necessary size, locations, climate-controlled, etc. Storage.com’s rep made sure all of my questions were answered and the reservation was made.",
								author: "Hilary Martin",
								title: "Excellent help when I needed it!",
								imageSrc: AnnAlarcon
							},
							{
								body: "The person from Storage.com who assisted me was super helpful and offered several options. I saved a lot of money because of the great deals your company could offer, which were below market rate if I had dealt directly with the storage company.",
								author: "Pasadena Garden Club",
								title: "The person who assisted me was super…",
								imageSrc: SMurphy
							}
						]
					}
				}
			]
		},
		{
			title: "Kayak / Canoe FAQ",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Kayak Storage FAQs",
						faqs: KayakFAQs
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Kayak Storage",
						unitCards: [
							{
								category: "Recreational Storage",
								content:
									"How to Store Kayaks and Canoes: DIY Storage Solutions for Personal Water Crafts",
								date: "July 30, 2025",
								image: KayakStorage,
								url: "https://www.storage.com/blog/how-to-store-kayaks/"
							},
							{
								category: "Self Storage",
								content:
									"What is Drive-Up Storage? A Complete Guide to Convenient and Accessible Storage Solutions",
								date: "September 26, 2025",
								image: DriveUpStorage,
								url: "https://www.storage.com/blog/what-is-drive-up-storage/"
							},
							{
								category: "Self Storage",
								content:
									"Garage vs Storage Unit: 5 Benefits of Self Storage",
								date: "December 1, 2025",
								image: GarageStorage,
								url: "https://www.storage.com/blog/garage-vs-storage-unit-5-benefits-of-self-storage/"
							}
						]
					}
				}
			]
		},
		{
			title: "Kayak / Canoe PSP Links",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.kayak
						}
					}
				}
			]
		},
		{
			title: "Kayak / Canoe Storage Near you",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						pageType: "PSP",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPKayakContent;
